import { useEffect, useRef, useState } from 'react';
import { 
  FocusManager, 
  KeyboardNavigation, 
  ScreenReaderUtils, 
  MotionUtils,
  TouchUtils 
} from '../utils/accessibility';

/**
 * Hook for managing focus trapping in modals and dialogs
 */
export const useFocusTrap = (isActive: boolean) => {
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const cleanup = FocusManager.trapFocus(containerRef.current);
    return cleanup;
  }, [isActive]);

  return containerRef;
};

/**
 * Hook for keyboard navigation in lists and grids
 */
export const useKeyboardNavigation = (
  itemCount: number,
  orientation: 'horizontal' | 'vertical' | 'both' = 'both'
) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const handleKeyDown = (event: KeyboardEvent) => {
    const elements = itemRefs.current.filter(Boolean) as HTMLElement[];
    const newIndex = KeyboardNavigation.handleArrowNavigation(
      elements,
      currentIndex,
      event.key,
      orientation
    );
    
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex);
      event.preventDefault();
    }
  };

  const setItemRef = (index: number) => (element: HTMLElement | null) => {
    itemRefs.current[index] = element;
  };

  return {
    currentIndex,
    setCurrentIndex,
    handleKeyDown,
    setItemRef,
    itemRefs: itemRefs.current
  };
};

/**
 * Hook for managing live regions and announcements
 */
export const useLiveRegion = (regionId: string) => {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    ScreenReaderUtils.updateLiveRegion(regionId, message, priority);
  };

  const announceError = (message: string) => {
    announce(`Error: ${message}`, 'assertive');
  };

  const announceSuccess = (message: string) => {
    announce(`Success: ${message}`, 'polite');
  };

  return { announce, announceError, announceSuccess };
};

/**
 * Hook for detecting and responding to user preferences
 */
export const useUserPreferences = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    MotionUtils.prefersReducedMotion()
  );
  const [prefersHighContrast, setPrefersHighContrast] = useState(
    window.matchMedia('(prefers-contrast: high)').matches
  );
  const [prefersDarkMode, setPrefersDarkMode] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrastQuery = window.matchMedia('(prefers-contrast: high)');
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleMotionChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    const handleContrastChange = (e: MediaQueryListEvent) => {
      setPrefersHighContrast(e.matches);
    };

    const handleDarkModeChange = (e: MediaQueryListEvent) => {
      setPrefersDarkMode(e.matches);
    };

    motionQuery.addEventListener('change', handleMotionChange);
    contrastQuery.addEventListener('change', handleContrastChange);
    darkModeQuery.addEventListener('change', handleDarkModeChange);

    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
      darkModeQuery.removeEventListener('change', handleDarkModeChange);
    };
  }, []);

  return {
    prefersReducedMotion,
    prefersHighContrast,
    prefersDarkMode,
    getAnimationDuration: MotionUtils.getAnimationDuration,
    createMotionSafeConfig: MotionUtils.createMotionSafeConfig
  };
};

/**
 * Hook for managing keyboard vs mouse navigation
 */
export const useNavigationMode = () => {
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setIsKeyboardNavigation(true);
        document.body.classList.add('keyboard-navigation-active');
        document.body.classList.remove('mouse-navigation-active');
      }
    };

    const handleMouseDown = () => {
      setIsKeyboardNavigation(false);
      document.body.classList.add('mouse-navigation-active');
      document.body.classList.remove('keyboard-navigation-active');
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return isKeyboardNavigation;
};

/**
 * Hook for managing touch interactions and mobile accessibility
 */
export const useTouchAccessibility = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(TouchUtils.isTouchDevice());

  useEffect(() => {
    const handleTouchStart = () => {
      setIsTouchDevice(true);
    };

    document.addEventListener('touchstart', handleTouchStart, { once: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);

  const enhanceForTouch = (element: HTMLElement) => {
    if (isTouchDevice) {
      TouchUtils.ensureMinimumTouchTarget(element);
      TouchUtils.addTouchFeedback(element);
    }
  };

  return {
    isTouchDevice,
    enhanceForTouch
  };
};

/**
 * Hook for managing loading states with accessibility
 */
export const useAccessibleLoading = (isLoading: boolean, loadingMessage = 'Loading...') => {
  const { announce } = useLiveRegion('loading-region');

  useEffect(() => {
    if (isLoading) {
      announce(loadingMessage);
    }
  }, [isLoading, loadingMessage, announce]);

  const getLoadingProps = (element?: HTMLElement) => ({
    'aria-busy': isLoading,
    'aria-live': 'polite' as const,
    ...(isLoading && { 'aria-label': loadingMessage })
  });

  return { getLoadingProps };
};

/**
 * Hook for managing form accessibility
 */
export const useFormAccessibility = () => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { announceError, announceSuccess } = useLiveRegion('form-region');

  const setFieldError = (fieldName: string, error: string) => {
    setErrors(prev => ({ ...prev, [fieldName]: error }));
    announceError(`${fieldName}: ${error}`);
  };

  const clearFieldError = (fieldName: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  const clearAllErrors = () => {
    setErrors({});
  };

  const announceFormSuccess = (message: string) => {
    announceSuccess(message);
  };

  const getFieldProps = (fieldName: string, label: string) => {
    const hasError = !!errors[fieldName];
    const errorId = `${fieldName}-error`;
    const helpId = `${fieldName}-help`;

    return {
      'aria-label': label,
      'aria-invalid': hasError,
      'aria-describedby': hasError ? errorId : undefined,
      errorId,
      helpId,
      error: errors[fieldName]
    };
  };

  return {
    errors,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    announceFormSuccess,
    getFieldProps
  };
};

/**
 * Hook for managing skip links
 */
export const useSkipLinks = () => {
  useEffect(() => {
    const skipLinks = [
      { targetId: 'main-content', text: 'Skip to main content' },
      { targetId: 'navigation', text: 'Skip to navigation' },
      { targetId: 'footer', text: 'Skip to footer' }
    ];

    const container = document.createElement('div');
    container.className = 'skip-links-container';
    
    skipLinks.forEach(({ targetId, text }) => {
      const target = document.getElementById(targetId);
      if (target) {
        const skipLink = ScreenReaderUtils.createSkipLink(targetId, text);
        container.appendChild(skipLink);
      }
    });

    if (container.children.length > 0) {
      document.body.insertBefore(container, document.body.firstChild);
    }

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, []);
};